<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 d-inline-block mb-0">{{ $t("easmigration.title") }}</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- Tab strip. The repo has no tab component in use on any Dtac view, so
           this is two buttons over one v-if rather than a new dependency. -->
      <div class="mb-3">
        <base-button
          size="sm"
          :type="activeTab === 'migration' ? 'primary' : 'neutral'"
          @click="activeTab = 'migration'"
        >
          {{ $t("easmigration.tab_migration") }}
        </base-button>
        <base-button
          size="sm"
          :type="activeTab === 'logs' ? 'primary' : 'neutral'"
          @click="openLogsTab"
        >
          {{ $t("easmigration.tab_logs") }}
        </base-button>
      </div>

      <!-- ============================ MIGRATION ============================ -->
      <div v-if="activeTab === 'migration'">
        <card>
          <template slot="header">
            <h3 class="mb-0">{{ $t("easmigration.title") }}</h3>
            <p class="text-muted small mb-0">{{ $t("easmigration.help") }}</p>
          </template>

          <div class="form-group row">
            <div class="col-md-6">
              <base-input :label="$t('easmigration.corporate')">
                <el-select
                  v-model="enterpriseId"
                  filterable
                  class="select-primary"
                  :placeholder="$t('easmigration.corporate_placeholder')"
                  @change="onCorporateChange"
                >
                  <el-option
                    v-for="corp in mainCorporates"
                    :key="corp.ID"
                    :value="corp.ID"
                    :label="corporateLabel(corp)"
                  >
                  </el-option>
                </el-select>
              </base-input>
              <small class="text-muted" v-if="selectedCorporate">
                {{ $t("easmigration.current_eas") }}:
                {{
                  selectedCorporate.EasDisplayName ||
                    selectedCorporate.EasServer ||
                    $t("easmigration.no_eas")
                }}
              </small>
            </div>

            <div class="col-md-6">
              <!-- Disabled-when-full markup follows CreateCorporateV4.vue: a
                   configured capacity of 0 means the server has no ceiling. -->
              <base-input :label="$t('easmigration.target_eas')">
                <el-select
                  v-model="targetEas"
                  class="select-primary"
                  :placeholder="$t('easmigration.target_eas_placeholder')"
                  @change="onTargetChange"
                >
                  <el-option
                    v-for="srv in easServers"
                    :key="srv.Name"
                    :value="srv.Name"
                    :label="
                      (srv.DisplayName || srv.Name) +
                        ' (' + srv.Used + '/' + srv.Capacity + ')'
                    "
                    :disabled="srv.Capacity > 0 && srv.Used >= srv.Capacity"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>

          <div class="text-right">
            <!--
              The voicemail backup sits beside the preflight button rather than
              with the migration steps below it, because it is the one action on
              this page that has to happen BEFORE anything is committed to: the
              migration destroys every message stored on the source EAS Server,
              and once the delete pass has run there is nothing left to copy.

              Its disabled rule is deliberately weaker than the preflight
              button's. A backup reads one enterprise and cares nothing for
              where that enterprise is going, so requiring a target EAS server
              to be chosen first would gate a safety step behind a decision the
              operator has not had to make yet.

              vmOpen is the "a backup is running" guard. The popup below is
              non-dismissable until the run finishes, so while it is open a
              backup is by definition still in flight, and one flag covers both
              halves of the requirement without a second one to keep in step.
            -->
            <base-button
              v-if="isMigrationUser"
              type="neutral"
              class="mr-1"
              @click="startVoicemailBackup"
              :disabled="busy || vmOpen || !enterpriseId"
            >
              <i class="fa fa-envelope mr-1"></i>
              {{ $t("easmigration.vm_button") }}
            </base-button>
            <base-button
              type="primary"
              @click="runPreflight"
              :disabled="busy || !enterpriseId || !targetEas"
            >
              <i class="fa fa-stethoscope mr-1"></i>
              {{ $t("easmigration.check_button") }}
            </base-button>
          </div>
          <p
            v-if="isMigrationUser"
            class="text-muted small text-right mb-0 mt-2"
          >
            {{ $t("easmigration.vm_help") }}
          </p>
        </card>

        <!-- ---------------------------- preflight ---------------------------- -->
        <card v-if="preflight">
          <template slot="header">
            <h3 class="mb-0">{{ $t("easmigration.preflight_title") }}</h3>
            <p class="text-muted small mb-0">
              {{ preflight.EnterpriseName }} —
              {{ preflight.Source && (preflight.Source.DisplayName || preflight.Source.Name) }}
              &rarr;
              {{ preflight.Target && (preflight.Target.DisplayName || preflight.Target.Name) }}
            </p>
          </template>

          <div v-if="blocking.length > 0" class="mb-4">
            <h4 class="text-danger">{{ $t("easmigration.blocking_title") }}</h4>
            <ul class="list-unstyled mb-0">
              <li v-for="(f, i) in blocking" :key="'b' + i" class="mb-2">
                <span class="badge badge-danger mr-2">!</span>
                <span>{{ $t(f.Msg) }}</span>
                <small v-if="f.Cause" class="text-muted d-block ml-4">{{ f.Cause }}</small>
              </li>
            </ul>
          </div>

          <div class="mb-4">
            <h4>{{ $t("easmigration.warnings_title") }}</h4>
            <p v-if="warnings.length === 0" class="text-muted mb-0">
              {{ $t("easmigration.no_warnings") }}
            </p>
            <ul v-else class="list-unstyled mb-0">
              <li v-for="(f, i) in warnings" :key="'w' + i" class="mb-2">
                <span class="badge badge-warning mr-2">!</span>
                <span>{{ $t(f.Msg) }}</span>
                <small v-if="f.Cause" class="text-muted d-block ml-4">{{ f.Cause }}</small>
              </li>
            </ul>
          </div>

          <div class="row">
            <div class="col-md-6">
              <h4>{{ $t("easmigration.counts_title") }}</h4>
              <ul class="list-unstyled mb-0" v-if="preflight.Counts">
                <li>
                  {{ $t("easmigration.count_bg") }}:
                  <strong>{{ preflight.Counts.BusinessGroup }}</strong>
                </li>
                <li>
                  {{ $t("easmigration.count_numbers") }}:
                  <strong>{{ preflight.Counts.Numbers }}</strong>
                </li>
                <li>
                  {{ $t("easmigration.count_mlhg") }}:
                  <strong>{{ preflight.Counts.MlhgDummyPilotLines }}</strong>
                </li>
                <li>
                  {{ $t("easmigration.count_skipped_alt") }}:
                  <strong>{{ preflight.Counts.SkippedAlternative }}</strong>
                </li>
                <li>
                  {{ $t("easmigration.count_total") }}:
                  <strong>{{ preflight.Counts.Total }}</strong>
                </li>
              </ul>
            </div>
            <div class="col-md-6">
              <h4>{{ $t("easmigration.capacity_title") }}</h4>
              <ul class="list-unstyled mb-0" v-if="preflight.TargetCapacity">
                <li>
                  {{ $t("easmigration.capacity_capacity") }}:
                  <strong>{{
                    capacityCell(
                      preflight.TargetCapacity.Capacity,
                      preflight.TargetCapacity.TextKey
                    )
                  }}</strong>
                </li>
                <li>
                  {{ $t("easmigration.capacity_used") }}:
                  <strong>{{ preflight.TargetCapacity.Used }}</strong>
                </li>
                <li>
                  {{ $t("easmigration.capacity_available") }}:
                  <strong>{{
                    capacityCell(
                      preflight.TargetCapacity.Available,
                      preflight.TargetCapacity.TextKey
                    )
                  }}</strong>
                </li>
                <li>
                  {{ $t("easmigration.capacity_required") }}:
                  <strong>{{ preflight.TargetCapacity.Required }}</strong>
                </li>
              </ul>
            </div>
          </div>

          <div class="mt-3" v-if="subBranches.length > 0">
            <h4>{{ $t("easmigration.sub_branches") }}</h4>
            <span
              v-for="b in subBranches"
              :key="b.ID"
              class="badge badge-secondary mr-1"
            >{{ b.Name }}</span>
          </div>

          <div class="mt-4 text-right">
            <p
              v-if="preflight.CanMigrate"
              class="text-success small text-left mb-2"
            >
              {{ $t("easmigration.preflight_passed") }}
            </p>
            <p class="text-muted small text-left mb-2">
              {{ $t("easmigration.read_help") }}
            </p>
            <base-button
              type="primary"
              @click="runRead"
              :disabled="busy || !preflight.CanMigrate"
            >
              <i class="fa fa-download mr-1"></i>
              {{ $t("easmigration.read_button") }}
            </base-button>
          </div>
        </card>

        <!-- ------------------------- the current job ------------------------- -->
        <card v-if="job">
          <template slot="header">
            <div class="row align-items-center">
              <div class="col-8">
                <h3 class="mb-0">
                  {{ $t("easmigration.job_title") }}
                  <span class="badge ml-2" :class="jobBadge(job.Status)">
                    {{ jobStatusText(job.Status) }}
                  </span>
                </h3>
                <p class="text-muted small mb-0">
                  {{ $t("easmigration.job_id") }} #{{ job.Id }} ·
                  {{ job.EnterpriseName }} ·
                  {{ job.SourceDisplayName || job.SourceEas }} &rarr;
                  {{ job.TargetDisplayName || job.TargetEas }} ·
                  {{ $t("easmigration.attempt") }} {{ job.Attempt }}
                </p>
              </div>
              <div class="col-4 text-right">
                <base-button size="sm" type="neutral" @click="refreshStatus">
                  {{ $t("easmigration.refresh") }}
                </base-button>
              </div>
            </div>
          </template>

          <!-- Authorise step. Only a job resting at READ_OK can be authorised;
               CanStart is the server's own answer to that question. -->
          <div v-if="job.CanStart" class="mb-4">
            <h4>{{ $t("easmigration.authorise_title") }}</h4>
            <p class="text-muted mb-2">{{ $t("easmigration.authorise_help") }}</p>
            <p class="mb-1" v-if="job.ReadCompletedAt">
              {{ $t("easmigration.read_completed_at") }}: {{ job.ReadCompletedAt }}
              <small class="text-muted ml-2">
                ({{ $t("easmigration.read_age") }}
                {{ formatDuration(job.ReadAgeSeconds) }})
              </small>
            </p>
            <!-- The stale read is surfaced, never hidden: it is a warning and
                 not a refusal, and the operator is the one who decides. -->
            <div v-if="job.ReadStale" class="alert alert-warning py-2">
              <i class="fa fa-exclamation-triangle mr-1"></i>
              {{ $t(job.ReadStaleMsg || "errors.easmigration.read_stale") }}
            </div>

            <!-- Same four-pass strip the progress popup shows, so what the
                 operator agrees to here is the picture they then watch. -->
            <div class="pass-timeline mb-3">
              <div
                v-for="p in passSteps"
                :key="'auth-' + p.id"
                class="pass-step"
                :class="[p.outage ? 'pass-step-outage' : '', p.state]"
              >
                <span class="pass-step-name">{{ $t(p.label) }}</span>
                <small class="pass-step-hint">{{ $t(p.hint) }}</small>
              </div>
              <div class="pass-bracket">
                <i class="fa fa-plug mr-1"></i>
                {{ $t("easmigration.outage_bracket") }}
              </div>
            </div>

            <div class="text-right">
              <base-button
                type="danger"
                @click="confirmStart"
                :disabled="busy"
              >
                <i class="fa fa-play mr-1"></i>
                {{ $t("easmigration.start_button") }}
              </base-button>
            </div>
          </div>

          <div v-if="job.ErrorLog" class="alert alert-danger py-2">
            {{ errorLogText(job.ErrorLog) }}
          </div>

          <div class="row align-items-center mb-3">
            <div class="col-md-8">
              <base-progress
                :type="outageOpen ? 'danger' : 'primary'"
                :height="8"
                :value="progressPercent"
                :show-label="true"
                :label="phaseText(job.Phase)"
              >
                <span>{{ job.DoneItems }} / {{ job.TotalItems }}</span>
              </base-progress>
            </div>
            <div class="col-md-4 text-right">
              <span class="badge badge-success mr-1">
                {{ $t("easmigration.done_label") }} {{ job.DoneItems }}
              </span>
              <span class="badge badge-danger">
                {{ $t("easmigration.error_label") }} {{ job.ErrorItems }}
              </span>
            </div>
          </div>

          <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
            <h4 class="mb-0">{{ $t("easmigration.result_title") }}</h4>
            <div>
              <base-button
                size="sm"
                type="neutral"
                @click="runExport"
                :disabled="exporting || !migrationId"
              >
                {{ $t("export.button") }}
              </base-button>
              <base-button
                v-if="canRetry"
                size="sm"
                type="warning"
                @click="confirmRetry"
                :disabled="busy"
              >
                <i class="fa fa-redo mr-1"></i>
                {{ $t("easmigration.retry_button") }}
              </base-button>
            </div>
          </div>

          <!--
            The read is what failed, so /retry would answer 422
            errors.easmigration.read_required. Say what actually recovers it.
          -->
          <div v-if="needsReread" class="alert alert-warning py-2">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            {{ $t("easmigration.reread_required") }}
          </div>

          <p v-if="items.length === 0" class="text-muted">
            {{ $t("easmigration.no_items") }}
          </p>
          <el-table v-else :data="items" header-row-class-name="thead-light">
            <el-table-column
              :label="$t('easmigration.col_seq')"
              prop="Seq"
              min-width="70"
            ></el-table-column>
            <el-table-column
              :label="$t('easmigration.col_kind')"
              prop="Kind"
              min-width="110"
            ></el-table-column>
            <el-table-column
              :label="$t('easmigration.col_object')"
              prop="ObjectKey"
              min-width="220"
            ></el-table-column>
            <el-table-column
              :label="$t('easmigration.col_delete')"
              min-width="120"
            >
              <template slot-scope="{ row }">
                <span class="badge" :class="itemBadge(row.DeleteStatus)">
                  {{ itemStatusText(row.DeleteStatus) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('easmigration.col_create')"
              min-width="120"
            >
              <template slot-scope="{ row }">
                <span class="badge" :class="itemBadge(row.CreateStatus)">
                  {{ itemStatusText(row.CreateStatus) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('easmigration.col_repoint')"
              min-width="120"
            >
              <template slot-scope="{ row }">
                <span class="badge" :class="itemBadge(row.RepointStatus)">
                  {{ itemStatusText(row.RepointStatus) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('easmigration.col_verify')"
              min-width="120"
            >
              <template slot-scope="{ row }">
                <span class="badge" :class="itemBadge(row.VerifyStatus)">
                  {{ itemStatusText(row.VerifyStatus) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('easmigration.col_result_code')"
              prop="ResultCode"
              min-width="110"
            ></el-table-column>
            <el-table-column
              :label="$t('easmigration.col_error')"
              min-width="260"
            >
              <template slot-scope="{ row }">
                <small class="text-muted">{{ errorLogText(row.ErrorLog) }}</small>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('easmigration.col_updated')"
              prop="UpdatedAt"
              min-width="160"
            ></el-table-column>
            <!--
              What the read actually captured for this one object.

              The payloads are whole SOAP bodies and one item carries several of
              them — the CFS half, the EAS half, one row per service indication
              — so they are never part of the item list. This button is the only
              thing that asks for them, and it asks for one item at a time.
            -->
            <el-table-column
              :label="$t('easmigration.col_snapshot')"
              min-width="130"
            >
              <template slot-scope="{ row }">
                <base-button
                  size="sm"
                  type="neutral"
                  @click="openSnapshot(row)"
                  :disabled="loadingSnapshot"
                >
                  {{ $t("easmigration.view_snapshot") }}
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </card>
      </div>

      <!-- ============================== LOGS ============================== -->
      <div v-else>
        <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
          <template slot="header">
            <div class="row">
              <div class="col-lg-6">
                <h3 class="mb-0">{{ $t("easmigration.logs_title") }}</h3>
                <p class="text-muted small mb-0">{{ $t("easmigration.log_help") }}</p>
              </div>
              <div class="col-lg-6 text-right">
                <base-button size="sm" type="primary" @click="searchLogs">
                  {{ $t("easmigration.search") }}
                </base-button>
              </div>
            </div>
          </template>

          <div class="px-3 pb-3">
            <div class="form-group row">
              <div class="col-md-3">
                <base-input :label="$t('easmigration.job_id')">
                  <el-select
                    v-model="migrationId"
                    filterable
                    class="select-primary"
                    @change="onLogJobChange"
                  >
                    <el-option
                      v-for="j in history"
                      :key="j.Id"
                      :value="j.Id"
                      :label="historyLabel(j)"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-md-3">
                <base-input :label="$t('easmigration.log_level')">
                  <el-select v-model="logLevel" class="select-primary">
                    <el-option
                      v-for="opt in logLevels"
                      :key="opt.value"
                      :value="opt.value"
                      :label="opt.label ? $t(opt.label) : opt.value"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-md-3">
                <base-input :label="$t('easmigration.log_attempt')">
                  <el-select v-model="logAttempt" class="select-primary">
                    <el-option
                      :value="''"
                      :label="$t('easmigration.log_attempt_all')"
                    ></el-option>
                    <el-option
                      v-for="a in attemptOptions"
                      :key="a"
                      :value="a"
                      :label="String(a)"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>
              <!--
                Phase and function, offered from what THIS job actually wrote.

                The lists come from the endpoint rather than from a table in
                this file, so a job that stopped before create_subs does not
                offer create_subs, and a historical row carrying the retired
                `extension` phase appears in the picker because the data has it.
                phaseText() names that one through the retired-step label it
                already owns, so it reads the same in the filter as it does in
                the table it filters.
              -->
              <div class="col-md-3">
                <base-input :label="$t('easmigration.log_phase')">
                  <el-select v-model="logPhase" class="select-primary">
                    <el-option
                      :value="''"
                      :label="$t('easmigration.log_phase_all')"
                    ></el-option>
                    <el-option
                      v-for="p in phaseOptions"
                      :key="p"
                      :value="p"
                      :label="phaseText(p)"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-md-3">
                <base-input :label="$t('easmigration.log_func')">
                  <!-- filterable: one job writes a few dozen distinct function
                       names, which is past the length a plain list is usable
                       at. Same treatment the job picker above already gets. -->
                  <el-select v-model="logFunc" filterable class="select-primary">
                    <el-option
                      :value="''"
                      :label="$t('easmigration.log_func_all')"
                    ></el-option>
                    <el-option
                      v-for="f in funcOptions"
                      :key="f"
                      :value="f"
                      :label="f"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
            <p v-if="!migrationId" class="text-muted mb-0">
              {{ $t("easmigration.select_job_first") }}
            </p>
          </div>

          <el-table
            :data="logs"
            header-row-class-name="thead-light"
            v-loading="loadingLogs"
          >
            <el-table-column
              :label="$t('easmigration.col_time')"
              prop="LoggedAt"
              min-width="180"
            ></el-table-column>
            <el-table-column
              :label="$t('easmigration.attempt')"
              prop="Attempt"
              min-width="80"
            ></el-table-column>
            <el-table-column
              :label="$t('easmigration.col_phase')"
              min-width="160"
            >
              <template slot-scope="{ row }">{{ phaseText(row.Phase) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('easmigration.col_level')"
              min-width="90"
            >
              <template slot-scope="{ row }">
                <span class="badge" :class="levelBadge(row.Level)">{{ row.Level }}</span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('easmigration.col_func')"
              prop="FuncName"
              min-width="180"
            ></el-table-column>
            <el-table-column
              :label="$t('easmigration.col_message')"
              min-width="360"
            >
              <template slot-scope="{ row }">
                <small class="text-muted">{{ row.Message }}</small>
              </template>
            </el-table-column>
            <el-table-column :label="$t('easmigration.col_soap')" min-width="110">
              <template slot-scope="{ row }">
                <base-button
                  v-if="row.Soap"
                  size="sm"
                  type="neutral"
                  @click="openSoap(row)"
                >
                  {{ $t("easmigration.view_soap") }}
                </base-button>
              </template>
            </el-table-column>
          </el-table>

          <p v-if="!loadingLogs && logs.length === 0" class="text-muted px-3 pt-3">
            {{ $t("easmigration.no_logs") }}
          </p>

          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            v-if="logTotal > 0"
          >
            <p class="card-category">
              {{ $t("data") }} {{ logRangeFrom + 1 }} {{ $t("to") }}
              {{ logRangeTo }} {{ $t("of") }} {{ logTotal }}
            </p>
            <base-pagination
              class="pagination-no-border"
              :value="logPage"
              @input="onLogPageChange"
              :per-page="logPerPage"
              :total="logTotal"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>

    <!-- ---------------------------- progress popup ----------------------------
         Non-dismissable on purpose: `:show` is bound one-way, so the backdrop
         mousedown Modal.vue emits is ignored and there is no close button. -->
    <modal :show="progressOpen" :show-close="false">
      <div class="py-2">
        <h4 class="mb-1">
          <i class="fa fa-spinner fa-spin mr-2 text-primary"></i>
          {{ job && job.Authorised
            ? $t("easmigration.progress_title")
            : $t("easmigration.progress_read_title") }}
        </h4>
        <p class="text-muted mb-3" v-if="job">
          {{ job.EnterpriseName }} ·
          {{ job.SourceDisplayName || job.SourceEas }} &rarr;
          {{ job.TargetDisplayName || job.TargetEas }}
        </p>

        <!-- The outage banner sits ABOVE the phase line and the bar on purpose.
             The bar and the item counter both read as steady progress, which is
             exactly the impression that makes an operator treat this as a
             per-number blip. Whether the corporate is currently reachable is a
             different question from how far along the job is, and it is the
             more urgent one, so it is answered first and in its own colour. -->
        <div v-if="outageState !== 'none'" class="alert py-2 mb-3" :class="outageAlertClass">
          <h5 v-if="outageState !== 'closed'" class="text-white mb-1">
            <i class="fa fa-plug mr-1"></i>
            {{ $t("easmigration.outage_title") }}
          </h5>
          <p class="mb-1 small">
            <i v-if="outageState === 'closed'" class="fa fa-check mr-1"></i>
            {{ $t(outageMessageKey) }}
          </p>
          <p v-if="outageOpen" class="mb-0 small">
            {{ $t("easmigration.outage_window_hint") }}
          </p>
        </div>

        <!-- The four passes, with one unbroken red band under the two the
             corporate is down for. The phases listed flat would give no clue
             that the middle eight are one continuous outage; the band is what
             turns "which step are we on" into "how much longer is it down". -->
        <p class="mb-1">
          <strong>{{ $t("easmigration.pass_label") }}:</strong>
        </p>
        <div class="pass-timeline mb-3">
          <div
            v-for="p in passSteps"
            :key="'run-' + p.id"
            class="pass-step"
            :class="[p.outage ? 'pass-step-outage' : '', p.state]"
          >
            <span class="pass-step-name">{{ $t(p.label) }}</span>
            <small class="pass-step-hint">{{ $t(p.hint) }}</small>
          </div>
          <div class="pass-bracket">
            <i class="fa fa-plug mr-1"></i>
            {{ $t("easmigration.outage_bracket") }}
          </div>
        </div>

        <p class="mb-1">
          <strong>{{ $t("easmigration.phase_label") }}:</strong>
          {{ job ? phaseText(job.Phase) : "" }}
        </p>

        <base-progress
          :type="outageOpen ? 'danger' : 'primary'"
          :height="10"
          :value="progressPercent"
          :show-label="true"
          :label="$t('easmigration.total_label')"
        >
          <span v-if="job">{{ job.DoneItems }} / {{ job.TotalItems }}</span>
        </base-progress>

        <p class="mb-1 mt-3" v-if="currentObject">
          <strong>{{ $t("easmigration.current_object") }}:</strong>
          {{ currentObject }}
        </p>
        <p class="mb-1">
          <strong>{{ $t("easmigration.elapsed") }}:</strong>
          {{ formatDuration(elapsed) }}
        </p>
        <p class="mb-0">
          <span class="badge badge-success mr-1">
            {{ $t("easmigration.done_label") }} {{ job ? job.DoneItems : 0 }}
          </span>
          <span class="badge badge-danger">
            {{ $t("easmigration.error_label") }} {{ job ? job.ErrorItems : 0 }}
          </span>
        </p>
      </div>
    </modal>

    <!-- ---------------------- voicemail backup popup ----------------------
         Same discipline as the migration popup above: `:show` is bound one-way,
         so the backdrop mousedown Modal.vue emits is ignored, and :show-close
         is false. The footer Close button appears only once the run has
         actually finished, because closing is what stops the poll — a modal
         that could be dismissed mid-run is precisely how a timer outlives the
         thing it was polling for.

         The modal IS the feature. A backup that reads a hundred mailboxes off
         one EAS host takes minutes, and the only question the operator has
         during those minutes is "is it still moving, and where". So the number
         currently being read is given its own line rather than being folded
         into a percentage. -->
    <modal :show="vmOpen" :show-close="false">
      <div class="py-2">
        <h4 class="mb-1">
          <i class="mr-2" :class="vmHeadingIcon"></i>
          {{ $t("easmigration.vm_title") }}
        </h4>
        <p class="text-muted mb-3" v-if="selectedCorporate">
          {{ selectedCorporate.Name }}
        </p>

        <!-- The four steps, same strip the migration passes use. No outage
             bracket: a backup only reads, so nothing goes off the air. -->
        <div class="pass-timeline mb-3">
          <div
            v-for="s in vmSteps"
            :key="'vm-' + s.id"
            class="pass-step"
            :class="s.state"
          >
            <span class="pass-step-name">{{ $t(s.label) }}</span>
            <small class="pass-step-hint">{{ $t(s.hint) }}</small>
          </div>
        </div>

        <base-progress
          :type="vmStage === 'error' ? 'danger' : 'primary'"
          :height="10"
          :value="vmPercent"
          :show-label="true"
          :label="$t('easmigration.vm_progress_label')"
        >
          <span>{{ vm.done }} / {{ vm.total }}</span>
        </base-progress>

        <!-- Only meaningful while the read is running: once the worker moves on
             to compressing, CurrentNumber is whatever it happened to stop on,
             and leaving it on screen would read as "still stuck there". -->
        <p class="mb-1 mt-3" v-if="vmStage === 'read'">
          <strong>{{ $t("easmigration.vm_current_number") }}:</strong>
          {{ vm.current || $t("easmigration.vm_current_none") }}
        </p>

        <p class="mb-1" :class="vmStage === 'read' ? '' : 'mt-3'">
          <strong>{{ $t("easmigration.vm_elapsed") }}:</strong>
          {{ formatDuration(vm.elapsed) }}
          <span v-if="vm.size > 0" class="ml-3">
            <strong>{{ $t("easmigration.vm_size") }}:</strong>
            {{ formatBytes(vm.size) }}
          </span>
        </p>

        <p class="mb-0">
          <span class="badge badge-success mr-1">
            {{ $t("easmigration.vm_messages") }} {{ vm.messages }}
          </span>
          <span class="badge" :class="vm.errors > 0 ? 'badge-danger' : 'badge-light'">
            {{ $t("easmigration.vm_errors") }} {{ vm.errors }}
          </span>
        </p>

        <!-- Finished, but not every mailbox made it into the zip. Saying so is
             the whole reason ErrorCount is on screen: an operator who then runs
             the migration on the strength of "backup complete" loses exactly
             the messages this line is about, and this banner is deliberately
             above the Download button rather than beside it — the warning has
             to be read BEFORE the migration is authorised, not after the file
             lands in the browser. -->
        <div
          v-if="vmStage === 'ready' && vm.errors > 0"
          class="alert alert-warning py-2 mt-3 mb-0"
        >
          <i class="fa fa-exclamation-triangle mr-1"></i>
          {{ $t("easmigration.vm_partial") }}
        </div>

        <div
          v-if="vmStage === 'ready' && vm.errors === 0"
          class="alert alert-success py-2 mt-3 mb-0"
        >
          <i class="fa fa-check mr-1"></i>
          {{ $t("easmigration.vm_ready_text") }}
        </div>


        <!-- The failure half. The title is whatever the server named the
             failure; ErrorLog is rendered verbatim underneath rather than
             summarised, because it is the only record of WHICH mailboxes the
             worker could not read. -->
        <div v-if="vmStage === 'error'" class="mt-3">
          <div class="alert alert-danger py-2 mb-2">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            {{ vmErrorTitle || $t("easmigration.vm_error_title") }}
          </div>
          <pre v-if="vmErrorDetail" class="export-error-detail mb-2">{{ vmErrorDetail }}</pre>
          <div v-if="vm.errorLog">
            <p class="mb-1">
              <strong>{{ $t("easmigration.vm_error_log_title") }}</strong>
            </p>
            <pre class="export-error-detail">{{ vmErrorLogText(vm.errorLog) }}</pre>
          </div>
        </div>
      </div>

      <template slot="footer">
        <!-- Offered from Downloadable (via vmCanDownload), never from the
             phase: a job sits at `ready` in the database long after the
             retention sweep has taken its zip, and a button that answers 422 is
             worse than no button. Nothing is fetched automatically — the
             archive stays on the server either way, and the log page offers the
             same download later. -->
        <button
          v-if="vmCanDownload"
          type="button"
          class="btn btn-primary mr-auto"
          :disabled="vmDownloading"
          @click="downloadVoicemailBackup"
        >
          <i
            class="mr-1"
            :class="vmDownloading ? 'fa fa-spinner fa-spin' : 'fa fa-download'"
          ></i>
          {{ $t("easmigration.vm_download_button") }}
        </button>
        <button
          v-if="vmFinished"
          type="button"
          class="btn btn-link ml-auto"
          @click="closeVoicemailBackup"
        >
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!-- export progress / error, same treatment as the other v4 list pages -->
    <modal :show.sync="exporting" :show-close="false" modal-classes="modal-sm">
      <div class="text-center py-3">
        <i class="fa fa-spinner fa-spin fa-2x text-primary mb-2"></i>
        <h4 class="mb-1">{{ $t("export.processing") }}</h4>
        <p class="text-muted mb-0">{{ $t("export.processing_text") }}</p>
      </div>
    </modal>

    <modal
      :show="exportError !== null"
      @close="clearExportError"
      modal-classes="modal-secondary"
    >
      <template slot="header">
        <h5 class="modal-title text-danger">
          <i class="fa fa-exclamation-triangle mr-2"></i>
          {{ exportError && exportError.title }}
        </h5>
      </template>
      <pre
        v-if="exportError && exportError.detail"
        class="export-error-detail"
      >{{ exportError.detail }}</pre>
      <template slot="footer">
        <button type="button" class="btn btn-link ml-auto" @click="clearExportError">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!-- full SOAP envelope, matching the LogsTables.vue treatment -->
    <modal :show.sync="showSoapModal" modal-classes="modal-lg">
      <template slot="header">
        <h5 class="modal-title">
          {{ $t("easmigration.soap_title") }}
          <small v-if="soapFuncName" class="text-muted">— {{ soapFuncName }}</small>
        </h5>
      </template>

      <pre class="xml-detail">{{ soapPretty }}</pre>

      <template slot="footer">
        <button type="button" class="btn btn-secondary" @click="copySoap">
          <i class="far fa-copy mr-1"></i>{{ $t("easmigration.copy_soap") }}
        </button>
        <button
          type="button"
          class="btn btn-link ml-auto"
          @click="showSoapModal = false"
        >
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!--
      What the read pulled for one work item — the same large modal and the same
      dark XML block the logs tab gives an envelope, because it is the same kind
      of thing being read.

      An item has more than one repository behind it, so this is a list rather
      than a single body: the CFS half and the EAS half are separate rows, and
      each service indication within a half is its own row too. Each is labelled
      with the half, the service indication and the userIdentity it was pulled
      from, which together are the only way to tell two otherwise similar
      envelopes apart.
    -->
    <modal :show.sync="showSnapshotModal" modal-classes="modal-lg">
      <template slot="header">
        <h5 class="modal-title">
          {{ $t("easmigration.snapshot_title") }}
          <small v-if="snapshotObjectKey" class="text-muted">
            — {{ snapshotObjectKey }}
          </small>
        </h5>
      </template>

      <div v-if="loadingSnapshot" class="text-center py-4">
        <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
      </div>
      <div v-else>
        <p v-if="snapshotRows.length === 0" class="text-muted mb-0">
          {{ $t("easmigration.snapshot_empty") }}
        </p>
        <div v-else>
          <p class="text-muted small mb-1">{{ $t("easmigration.snapshot_masked") }}</p>
          <!-- Said out loud rather than left to be noticed: an operator reading
               this modal to find out what the read captured must not mistake a
               clipped list for the whole of it. -->
          <p v-if="snapshotTotal > snapshotRows.length" class="text-warning small">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            {{
              $t("easmigration.snapshot_truncated", {
                shown: snapshotRows.length,
                total: snapshotTotal
              })
            }}
          </p>
          <div
            v-for="row in snapshotRows"
            :key="row.Key"
            class="snapshot-entry mb-3"
          >
            <div class="d-flex flex-wrap align-items-center mb-1">
              <span class="badge mr-2" :class="halfBadge(row.Half)">
                {{ halfText(row.Half) }}
              </span>
              <strong class="mr-2">{{ row.ServiceIndication }}</strong>
              <small class="text-muted mr-2">{{ row.UserIdentity }}</small>
              <small class="text-muted mr-2">
                {{ $t("easmigration.snapshot_seq") }} {{ row.SequenceNumber }}
              </small>
              <small class="text-muted">{{ row.PulledAt }}</small>
              <button
                type="button"
                class="btn btn-sm btn-secondary ml-auto"
                @click="copySnapshotRow(row)"
              >
                <i class="far fa-copy mr-1"></i>{{ $t("easmigration.copy_soap") }}
              </button>
            </div>
            <pre class="xml-detail">{{ row.Pretty }}</pre>
          </div>
        </div>
      </div>

      <template slot="footer">
        <button
          type="button"
          class="btn btn-link ml-auto"
          @click="showSnapshotModal = false"
        >
          {{ $t("close") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import Modal from "@/components/Modal";
import { BasePagination } from "@/components";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import exportMixin, {
  describeExportError,
  downloadBlob,
  isJsonBody,
  readBlobAsJSON
} from "./exportMixin";
// The voicemail job wire contract, shared with VoicemailBackupLog.vue. Field
// names, phase names and the normaliser live there and nowhere else.
import {
  VM_FIELDS,
  VM_KIND_BACKUP,
  VM_PHASE_COMPRESS,
  VM_PHASE_ERROR,
  VM_PHASE_READ,
  VM_PHASE_READY,
  VM_POLL_MS,
  VM_STATUS_FAILED,
  VM_ZIP_MIME,
  formatVmBytes,
  formatVmDuration,
  normaliseVmJob,
  vmErrorKeyForKind,
  vmNum
} from "./voicemailJob";
import { WebPortalService } from "../../resource";

// Job statuses, mirroring EAS_MIGRATION_STATUS_* in WPEasMigrationV4.go.
const JOB_QUEUED = 0;
const JOB_RUNNING = 1;
const JOB_SUCCESS = 2;
const JOB_FAILED = 3;
const JOB_READ_OK = 4;

// Item statuses, mirroring EasMigrationItem*V4 in WPEasMigrationStoreV4.go.
const ITEM_PENDING = 0;
const ITEM_DONE = 1;
const ITEM_NOT_ATTEMPTED = 3;
const ITEM_FAILED = 5;
const ITEM_SKIPPED = 9;

// Which pass of the job each phase belongs to, mirroring EasMigrationPhasesV4
// and EasMigrationPhaseIsOutageV4 in controllers/WPEasMigrationStoreV4.go.
//
// The four passes are a presentation of the backend's phase list, not a second
// opinion about it: "delete" and "create" together are the phases during which
// the corporate is off the air. That correspondence is the whole point of the
// strip — the operator sees one continuous red band covering both passes
// rather than a row of equal-looking steps, because the corporate is down for
// the whole of it.
//
// delete_extension and create_extension are mapped into those two passes
// rather than left out. They bracket the run — the extension list is the first
// thing cleared from the source and the last thing written back to the target —
// so they are the outer edges of the band, not something beside it. Should the
// backend's own outage predicate ever exclude them, this table is the wrong
// place to encode that disagreement: over-stating the window by one repository
// write is safe, and under-stating it is what stops an operator booking a long
// enough maintenance window.
//
// A phase this table does not know maps to no pass at all. That is deliberate:
// the outage state then falls back to what the item rows actually show
// (see outageState), so a backend that adds a phase degrades to a weaker
// signal instead of confidently rendering "nothing is wrong".
const PASS_READ = "read";
const PASS_DELETE = "delete";
const PASS_CREATE = "create";
const PASS_SETTLE = "settle";

const PHASE_PASS = {
  preflight: PASS_READ,
  snapshot: PASS_READ,
  plan: PASS_READ,
  delete_extension: PASS_DELETE,
  delete_subs: PASS_DELETE,
  delete_mlhg: PASS_DELETE,
  delete_bg: PASS_DELETE,
  create_bg: PASS_CREATE,
  create_mlhg: PASS_CREATE,
  create_subs: PASS_CREATE,
  create_extension: PASS_CREATE,
  commit: PASS_SETTLE,
  verify: PASS_SETTLE
  // "numbers" — the retired interleaved phase — is deliberately absent. It was
  // neither a delete pass nor a create pass; it was both, per number. Leaving
  // it out sends a historical row down the item-evidence path, which is the
  // only thing that can describe what such a job actually left behind.
};

// The strip itself, in order. The keys are written out in full rather than
// built from PASS_* so scripts/check-i18n-keys.js can see them.
const PASS_STEPS = [
  {
    id: PASS_READ,
    label: "easmigration.pass_read",
    hint: "easmigration.pass_read_hint",
    outage: false
  },
  {
    id: PASS_DELETE,
    label: "easmigration.pass_delete",
    hint: "easmigration.pass_delete_hint",
    outage: true
  },
  {
    id: PASS_CREATE,
    label: "easmigration.pass_create",
    hint: "easmigration.pass_create_hint",
    outage: true
  },
  {
    id: PASS_SETTLE,
    label: "easmigration.pass_settle",
    hint: "easmigration.pass_settle_hint",
    outage: false
  }
];

const POLL_MS = 2000;
// /status caps per_page at 200 and has no offset for the item list, so this is
// as much of the work list as the page can ever hold. A 145-number corporate
// builds ~150 items, so one page holds the whole list for every corporate in
// production today; past that the table shows the 200 most recently touched
// items, which are the ones an operator is looking for anyway — but the
// "items remain" count is suppressed rather than computed over a partial page.
// See itemsComplete.
const STATUS_ITEMS = 200;
const LOG_PER_PAGE = 50;
// /snapshot caps per_page at 50 and the modal has no pager, so this is the
// endpoint's ceiling rather than a page size. An item's snapshot is one row per
// repository — six for a subscriber, doubled by each re-read of the same job —
// so 50 holds every item this system produces; the modal still compares Total
// against what arrived rather than assuming that.
const SNAPSHOT_ROWS = 50;

// ---------------------------------------------------------------------------
// Voicemail backup
// ---------------------------------------------------------------------------
//
// A migration destroys every voicemail message on the source EAS Server, so
// the operator takes a copy of the whole enterprise first.
//
// The archive is KEPT. /backup/start opens a job, /job/status is polled while
// the worker reads number by number and then compresses, and the run ends at
// phase `ready` with the zip sitting on the server. It is NOT downloaded
// automatically and nothing deletes it: the operator presses Download when
// they want it, here or later from the Voicemail Backup Log page, and the same
// archive is what a restore replays after the migration. That is why there is
// no download phase and no clear phase — an archive this page had downloaded
// and then deleted could not be restored from.
//
// The wire field names live in voicemailJob.js, shared with the log page, so a
// rename on the backend is one line in one file for both.

// The three steps the operator watches, in order.
//
// Unlike the migration's four passes these ARE the backend's phases, one for
// one: read -> compress -> ready. The page adds no step of its own any more,
// because it no longer does work of its own after the zip exists.
//
// No outage bracket: a backup only reads, so nothing goes off the air.
const VM_STEPS = [
  {
    id: VM_PHASE_READ,
    label: "easmigration.vm_step_read",
    hint: "easmigration.vm_step_read_hint"
  },
  {
    id: VM_PHASE_COMPRESS,
    label: "easmigration.vm_step_compress",
    hint: "easmigration.vm_step_compress_hint"
  },
  {
    id: VM_PHASE_READY,
    label: "easmigration.vm_step_ready",
    hint: "easmigration.vm_step_ready_hint"
  }
];
const VM_STEP_ORDER = VM_STEPS.map(s => s.id);

export default {
  name: "eas-migration",
  mixins: [exportMixin],
  components: {
    RouteBreadCrumb,
    Modal,
    BasePagination,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  data() {
    return {
      activeTab: "migration",

      enterpriseId: "",
      targetEas: "",
      preflight: null,

      migrationId: 0,
      job: null,
      items: [],
      history: [],

      // busy guards every mutating button so a double click cannot queue two
      // reads or two authorisations.
      busy: false,
      progressOpen: false,
      // elapsed is seeded from the server's ElapsedSeconds on every poll and
      // ticked locally in between, so the timer moves every second without
      // asking the server every second.
      elapsed: 0,
      pollTimer: null,
      tickTimer: null,
      // killed is set in beforeDestroy. An in-flight /status can resolve after
      // the component is gone; without this it would schedule another poll.
      killed: false,

      logs: [],
      logTotal: 0,
      logPage: 1,
      logPerPage: LOG_PER_PAGE,
      logLevel: "",
      logAttempt: "",
      logPhase: "",
      logFunc: "",
      // The distinct phase and func_name values THIS job wrote, as /log
      // reports them in Phases and Functions. Never a table in this file: a job
      // that stopped at delete_bg must not be offered create_subs, and a
      // historical row carrying the retired `extension` phase must be offered,
      // which only the data can decide.
      phaseFacets: [],
      funcFacets: [],
      loadingLogs: false,
      logLevels: [
        { value: "", label: "easmigration.log_level_all" },
        { value: "INFO", label: "" },
        { value: "WARN", label: "" },
        { value: "ERROR", label: "" }
      ],

      showSoapModal: false,
      soapPretty: "",
      soapFuncName: "",

      showSnapshotModal: false,
      loadingSnapshot: false,
      snapshotObjectKey: "",
      snapshotRows: [],
      snapshotTotal: 0,

      // ---------------------- voicemail backup ----------------------
      // vmOpen doubles as the "a backup is running" flag: the popup cannot be
      // dismissed until the run finishes, so it is open for exactly as long as
      // there is a run to guard against.
      vmOpen: false,
      vmJobId: 0,
      // "" | "read" | "compress" | "ready" | "error" — the backend's own
      // phases, no longer a mixture of those and this page's own steps.
      vmStage: "",
      // Which step was in progress when it failed, so the strip marks the right
      // one rather than resetting to the start.
      vmErrorStep: "",
      vmErrorTitle: "",
      vmErrorDetail: "",
      // True while the Download button's request is in flight, so a second
      // click cannot fetch the same zip twice.
      vmDownloading: false,
      vm: normaliseVmJob(null),
      vmPollTimer: null,

      exportFn: () =>
        WebPortalService.EasMigrationExportV4({
          migration_id: this.migrationId
        })
    };
  },
  computed: {
    ...mapGetters({
      corporates: "CorporateV4/getData",
      easServers: "Capacity/getData",
      // The same gate the route itself is behind (routes.js meta
      // isMigrationUser → Auth/isMigrationUser). Read here as well so the
      // backup button is gated by the same rule as every other action on this
      // page rather than by "you got here somehow".
      isMigrationUser: "Auth/isMigrationUser"
    }),
    // Only a main branch can be migrated; a sub-branch inherits its parent's
    // EAS server and is moved with it.
    mainCorporates() {
      return (this.corporates || []).filter(c => c && !c.UpperID);
    },
    selectedCorporate() {
      return this.mainCorporates.find(c => c.ID === this.enterpriseId) || null;
    },
    blocking() {
      return (this.preflight && this.preflight.Blocking) || [];
    },
    warnings() {
      return (this.preflight && this.preflight.Warnings) || [];
    },
    subBranches() {
      return (this.preflight && this.preflight.SubBranches) || [];
    },
    // base-progress has no :max, so the percentage is computed here — and the
    // divide is guarded, because a job whose work list is empty would otherwise
    // render NaN%.
    progressPercent() {
      if (!this.job || !this.job.TotalItems || this.job.TotalItems <= 0) return 0;
      const pct = Math.round((this.job.DoneItems / this.job.TotalItems) * 100);
      if (pct < 0) return 0;
      return pct > 100 ? 100 : pct;
    },
    // Items come back ordered by updated_at DESC, so the head of the list is
    // whatever the worker touched last.
    currentObject() {
      return this.items.length > 0 ? this.items[0].ObjectKey : "";
    },

    // ------------------------- the outage window -------------------------
    //
    // Under the old interleaved order each number was down for the seconds
    // between its own delete and its own repoint, and nothing on this page had
    // to say so. Under the new order everything is deleted from the source
    // before anything is created on the target, so the corporate is off the
    // air from the first delete until the last create — the length of the
    // whole run. An operator who reads the progress bar as "each number blips"
    // will not book a maintenance window, so the page has to say which of the
    // two it is, in words, while it is happening.

    // Which of the four passes the job is in, or "" when it cannot be told.
    currentPass() {
      if (!this.job || !this.job.Phase) return "";
      return PHASE_PASS[this.job.Phase] || "";
    },

    // What the item rows prove, independently of the phase name.
    //
    // This is the fallback for a phase PHASE_PASS does not recognise — a
    // retired `numbers` row, or a phase a future backend adds — and it is only
    // ever used to open the window, never to close it. /status truncates the
    // item list at STATUS_ITEMS ordered by updated_at DESC, so the rows in hand
    // are the most recently touched ones, which are exactly the ones carrying
    // a completed delete. Truncation can therefore hide evidence that the
    // corporate is down; it cannot invent it. Erring towards "still down" is
    // the safe direction for this particular claim.
    deletesLanded() {
      return this.items.some(it => it.DeleteStatus === ITEM_DONE);
    },
    createsOutstanding() {
      return this.items.some(
        it => it.CreateStatus !== ITEM_DONE && it.CreateStatus !== ITEM_SKIPPED
      );
    },

    // "none" | "pending" | "open" | "closed".
    //
    //   none     nothing destructive has been authorised, so nothing can be
    //            down. The read half only ever pulls.
    //   pending  authorised, but still in preflight/snapshot/plan — the phases
    //            that exist precisely so that everything able to refuse the job
    //            happens while the corporate is still whole.
    //   open     between the first delete and the last create.
    //   closed   every create has landed; commit and verify touch no EAS.
    outageState() {
      if (!this.job || this.job.Authorised !== true) return "none";
      if (this.job.Status === JOB_SUCCESS) return "closed";

      const pass = this.currentPass;
      if (pass === PASS_DELETE || pass === PASS_CREATE) return "open";
      if (pass === PASS_SETTLE) return "closed";
      // PASS_READ on an authorised job: the destructive half has not started.
      if (pass === PASS_READ) return "pending";

      // Phase unrecognised — believe the item rows instead of guessing.
      if (this.deletesLanded && this.createsOutstanding) return "open";
      if (this.deletesLanded) return "closed";
      return "pending";
    },
    outageOpen() {
      return this.outageState === "open";
    },
    // The sentence under the banner heading. The failed case is the one that
    // matters most: the job has stopped, the popup has closed with it, and the
    // corporate stays down until a retry finishes. It must not read like any
    // other failure on this page.
    outageMessageKey() {
      if (this.outageState === "closed") return "easmigration.outage_closed";
      if (this.outageState === "pending") return "easmigration.outage_pending";
      if (this.job && this.job.Status === JOB_FAILED) {
        return "easmigration.outage_open_failed";
      }
      if (this.currentPass === PASS_CREATE) {
        return "easmigration.outage_open_create";
      }
      return "easmigration.outage_open_delete";
    },
    outageAlertClass() {
      return this.outageState === "closed" ? "alert-success" : "alert-danger";
    },
    // The four-pass strip, each step marked done / current / ahead.
    //
    // On the authorise card the job is still resting at READ_OK, so "read" is
    // the current step and the two outage passes are ahead — the strip is a
    // description of what the button is about to start. When the phase is not
    // one this page knows, no step is marked current and all four read as
    // ahead, which is the honest rendering of "the position is unknown".
    passSteps() {
      const at = PASS_STEPS.findIndex(p => p.id === this.currentPass);
      return PASS_STEPS.map((p, i) => {
        let state = "pass-ahead";
        if (at >= 0) {
          if (i < at) state = "pass-done";
          else if (i === at) state = "pass-current";
        }
        return {
          id: p.id,
          label: p.label,
          hint: p.hint,
          outage: p.outage,
          state: state
        };
      });
    },
    // Retry is offered only for a FAILED job that was already authorised.
    //
    // A job that failed during its READ never reached authorisation — the
    // worker branches on authorised_at, not on status, so a failed read leaves
    // status = FAILED with authorised_at still NULL. /retry refuses exactly
    // that case with errors.easmigration.read_required, so showing the button
    // there dead-ends the operator on a 422. The recovery for a failed read is
    // to read again, which needsReread offers instead.
    canRetry() {
      return (
        !!this.job &&
        this.job.Status === JOB_FAILED &&
        this.job.Authorised === true
      );
    },
    // FAILED but never authorised: the read is what failed. /retry cannot help.
    needsReread() {
      return (
        !!this.job &&
        this.job.Status === JOB_FAILED &&
        this.job.Authorised !== true
      );
    },
    // An item is finished only when all four columns are Done or Skipped;
    // Pending, NotAttempted and Failed all mean there is work left. Same rule
    // as EasMigrationRemainingItemsV4 on the backend.
    //
    // This counts only the items /status actually returned, so it is the true
    // remaining count ONLY when the page holds every item — see itemsComplete,
    // which gates whether the number is shown at all.
    remainingItems() {
      return this.items.filter(it => {
        return [
          it.DeleteStatus,
          it.CreateStatus,
          it.RepointStatus,
          it.VerifyStatus
        ].some(s => s !== ITEM_DONE && s !== ITEM_SKIPPED);
      }).length;
    },
    // Whether the item list in hand is the whole work list.
    //
    // /status returns at most STATUS_ITEMS items and orders them
    // `updated_at DESC, id DESC`, so a job with more items than that drops the
    // least-recently-touched rows — which are precisely the untouched pending
    // ones. Counting over a truncated page would therefore undercount, and
    // undercount systematically in the dangerous direction, on the confirmation
    // for a destructive action. The remaining count is stated only when this is
    // true; otherwise the dialog states no number at all.
    //
    // job.TotalItems is the full row count (eas_migration.total_items, written
    // when the work list is built), so it is the right thing to compare against.
    // The backend has no field carrying the true remaining count today; when
    // ResEasMigrationStatusV4 grows one, read it and delete this guard.
    itemsComplete() {
      if (!this.job) return false;
      const total = this.job.TotalItems || 0;
      return this.items.length >= total;
    },
    attemptOptions() {
      const max = this.job ? this.job.Attempt : 0;
      const out = [];
      for (let i = 0; i <= max; i++) out.push(i);
      return out;
    },
    // The facets the endpoint returned, plus whatever is currently selected.
    //
    // A selection can outlive the list it came from: the option lists are built
    // over this migration narrowed by attempt, so moving to another attempt
    // rebuilds them around a different timeline. Without this the picker would
    // fall back to rendering the raw stored value — or nothing — while that
    // value was still the thing filtering the table.
    phaseOptions() {
      return this.withSelection(this.phaseFacets, this.logPhase);
    },
    funcOptions() {
      return this.withSelection(this.funcFacets, this.logFunc);
    },
    logRangeFrom() {
      return (this.logPage - 1) * this.logPerPage;
    },
    logRangeTo() {
      const tail = this.logRangeFrom + this.logs.length;
      return tail > this.logTotal ? this.logTotal : tail;
    },

    // ---------------------- voicemail backup ----------------------

    // The run has stopped, one way or the other. This is what unlocks the
    // Close button, and closing is what stops the poll — so a stage that is
    // not in this list must never reach the footer.
    vmFinished() {
      return this.vmStage === VM_PHASE_READY || this.vmStage === VM_PHASE_ERROR;
    },
    vmHeadingIcon() {
      if (this.vmStage === VM_PHASE_ERROR) return "fa fa-times-circle text-danger";
      if (this.vmStage === VM_PHASE_READY) return "fa fa-check-circle text-success";
      return "fa fa-spinner fa-spin text-primary";
    },
    // Whether to offer the Download button.
    //
    // `Downloadable`, never the phase. The backend stats the file to compute
    // it, and a job can be at phase `ready` in the database with its archive
    // already reaped by the retention sweep. Offering a download that answers
    // 422 is worse than not offering one, on the modal whose whole job is
    // telling the operator whether a copy exists before they authorise a
    // migration that destroys the originals.
    vmCanDownload() {
      return (
        this.vmStage === VM_PHASE_READY &&
        this.vm.downloadable &&
        this.vmJobId > 0
      );
    },
    // base-progress has no :max, and a backup of an enterprise whose number
    // list has not been counted yet has total 0 — guard the divide.
    vmPercent() {
      if (this.vmStage === VM_PHASE_READY) return 100;
      if (!this.vm.total || this.vm.total <= 0) return 0;
      const pct = Math.round((this.vm.done / this.vm.total) * 100);
      if (pct < 0) return 0;
      return pct > 100 ? 100 : pct;
    },
    // Each step marked done / current / failed / ahead. On error the failed
    // step keeps its position and the ones before it stay done, so the strip
    // still says how far the run got.
    //
    // `ready` is both the last step and the terminal success, so it marks the
    // WHOLE strip done rather than leaving itself as the current step — a strip
    // whose last box is still pulsing reads as "not finished yet", which is
    // exactly the opposite of what phase ready means.
    vmSteps() {
      const active =
        this.vmStage === VM_PHASE_ERROR ? this.vmErrorStep : this.vmStage;
      const at =
        this.vmStage === VM_PHASE_READY
          ? VM_STEP_ORDER.length
          : VM_STEP_ORDER.indexOf(active);
      return VM_STEPS.map((s, i) => {
        let state = "pass-ahead";
        if (at >= 0) {
          if (i < at) state = "pass-done";
          else if (i === at) {
            state =
              this.vmStage === VM_PHASE_ERROR ? "pass-failed" : "pass-current";
          }
        }
        return { id: s.id, label: s.label, hint: s.hint, state: state };
      });
    }
  },
  mounted() {
    this.listCorporates();
    this.listCapacity();
    // A page refresh in the middle of a run must not lose the view: pick the
    // newest job back up and, if it is still in flight, reopen the popup.
    this.resumeActiveJob();
  },
  beforeDestroy() {
    // RecordTables.vue omits this and keeps polling after the user navigates
    // away. Both timers are cleared, and `killed` stops an already-dispatched
    // request from scheduling the next one when it resolves.
    this.killed = true;
    this.stopPolling();
    // The backup runs on its own timer and must be torn down on the same
    // event. `killed` is shared: it is set here and nowhere else, and every
    // await in the backup flow re-checks it before touching state or
    // scheduling anything.
    this.stopVmPolling();
    this.vmOpen = false;
    // Modal.vue puts `modal-open` on <body> while it is shown and takes it off
    // in a watcher. Navigating away with the progress popup open destroys the
    // modal before that watcher can fire, which would leave the next page
    // unable to scroll, so the class comes off here.
    this.progressOpen = false;
    if (typeof document !== "undefined") {
      document.body.classList.remove("modal-open");
    }
  },
  methods: {
    ...mapActions({
      listCorporates: "CorporateV4/listData",
      listCapacity: "Capacity/listData"
    }),

    // ------------------------------ labels ------------------------------
    corporateLabel(corp) {
      const eas =
        corp.EasDisplayName || corp.EasServer || this.$t("easmigration.no_eas");
      return corp.Name + " — " + eas;
    },
    historyLabel(j) {
      return (
        "#" +
        j.Id +
        " " +
        j.EnterpriseName +
        " (" +
        this.jobStatusText(j.Status) +
        ")"
      );
    },
    capacityCell(v, textKey) {
      // Capacity / Available are null rather than 0 whenever there is no finite
      // ceiling, and TextKey then says which of the two reasons it was —
      // unlimited, or a target that is not in the config at all. Rendering a
      // bare 0 here would read as "server full".
      if (v === null || v === undefined) {
        return this.$t(textKey || "easmigration.capacity_unknown");
      }
      return String(v);
    },
    // One entry per phase the worker still runs, in EasMigrationPhasesV4
    // order, plus the two retired names `numbers` and `extension`.
    //
    // `numbers` is kept even though no worker writes it any more. It is the
    // retired interleaved phase, and the logs tab renders phaseText() for every
    // historical row: dropping it would turn every log line of every job run
    // before the ordering change into "unknown phase". Its wording says
    // retired so the old and new orders cannot be confused when both appear in
    // the same picker.
    phaseText(phase) {
      const keys = {
        preflight: "easmigration.phase_preflight",
        snapshot: "easmigration.phase_snapshot",
        plan: "easmigration.phase_plan",
        delete_extension: "easmigration.phase_delete_extension",
        delete_subs: "easmigration.phase_delete_subs",
        delete_mlhg: "easmigration.phase_delete_mlhg",
        delete_bg: "easmigration.phase_delete_bg",
        create_bg: "easmigration.phase_create_bg",
        create_mlhg: "easmigration.phase_create_mlhg",
        create_subs: "easmigration.phase_create_subs",
        create_extension: "easmigration.phase_create_extension",
        commit: "easmigration.phase_commit",
        verify: "easmigration.phase_verify",
        numbers: "easmigration.phase_numbers",
        // The bare `extension` name is retired, and it keeps a label of its own
        // rather than borrowing either of the two above. A row carrying it was
        // written by the old check-and-restore step, which ran once at the end
        // and wrote only when something was already missing — not the same work
        // as delete_extension or create_extension, and naming it as either
        // would describe a historical job as having done something it did not.
        // The retired-step wording says only that the step no longer exists,
        // which is all that can honestly be said about it.
        extension: "easmigration.phase_retired"
      };
      const key = keys[phase];
      return key ? this.$t(key) : this.$t("easmigration.phase_unknown");
    },
    jobStatusText(status) {
      switch (status) {
        case JOB_QUEUED:
          return this.$t("easmigration.status_queued");
        case JOB_RUNNING:
          return this.$t("easmigration.status_running");
        case JOB_SUCCESS:
          return this.$t("easmigration.status_success");
        case JOB_FAILED:
          return this.$t("easmigration.status_failed");
        case JOB_READ_OK:
          return this.$t("easmigration.status_read_ok");
      }
      return this.$t("easmigration.status_unknown");
    },
    // READ_OK deliberately does NOT use badge-primary.
    //
    // $primary is DTAC navy #0c1026, and this theme overrides Bootstrap's
    // badge-variant mixin with `background: lighten($bg, 32%)` /
    // `color: saturate(darken($bg, 10%), 10)`. On a colour that dark the
    // lighten produces a solid #3344a2 and the darken clamps to black, so
    // badge-primary compiles to black on mid-navy at 2.49:1 — the heaviest
    // thing on the card, for the one status that means "nothing is happening,
    // it is waiting for you". badge-read-ok below is the light indigo pill
    // that replaces it; see the style block for the numbers.
    jobBadge(status) {
      if (status === JOB_SUCCESS) return "badge-success";
      if (status === JOB_FAILED) return "badge-danger";
      if (status === JOB_RUNNING) return "badge-info";
      if (status === JOB_READ_OK) return "badge-read-ok";
      return "badge-secondary";
    },
    itemStatusText(status) {
      switch (status) {
        case ITEM_PENDING:
          return this.$t("easmigration.item_pending");
        case ITEM_DONE:
          return this.$t("easmigration.item_done");
        case ITEM_NOT_ATTEMPTED:
          return this.$t("easmigration.item_not_attempted");
        case ITEM_FAILED:
          return this.$t("easmigration.item_failed");
        case ITEM_SKIPPED:
          return this.$t("easmigration.item_skipped");
      }
      return this.$t("easmigration.item_unknown");
    },
    itemBadge(status) {
      if (status === ITEM_DONE) return "badge-success";
      if (status === ITEM_FAILED) return "badge-danger";
      if (status === ITEM_SKIPPED) return "badge-secondary";
      if (status === ITEM_NOT_ATTEMPTED) return "badge-warning";
      return "badge-light";
    },
    levelBadge(level) {
      if (level === "ERROR") return "badge-danger";
      if (level === "WARN") return "badge-warning";
      return "badge-info";
    },
    // error_log is written as "errors.<key>|<detail>" — the key is translated
    // and the detail appended verbatim.
    //
    // `kind` is optional and only the voicemail callers pass it. One backend
    // key (errors.voicemailbackup.partial) means a read failure on a backup and
    // a write failure on a restore, so the Kind picks the wording; every other
    // key, and every migration caller, is untouched by the lookup.
    errorLogText(raw, kind) {
      if (!raw) return "";
      const cut = raw.indexOf("|");
      if (cut < 0) return raw;
      const key = vmErrorKeyForKind(raw.substring(0, cut), kind);
      const detail = raw.substring(cut + 1);
      const translated = this.$t(key);
      return (translated === key ? key : translated) + " " + detail;
    },
    // The backup popup's ErrorLog. Always a backup on this page, so the Kind is
    // supplied here rather than spelled as a bare string in the template.
    vmErrorLogText(raw) {
      return this.errorLogText(raw, VM_KIND_BACKUP);
    },
    // Both of these live in voicemailJob.js so the backup log page renders a
    // duration and a size exactly the way this page does. Thin wrappers rather
    // than a second copy: two implementations of "how big is the zip" is how
    // the same archive comes to be described two different ways on two pages.
    formatDuration(seconds) {
      return formatVmDuration(seconds);
    },
    formatBytes(bytes) {
      return formatVmBytes(bytes);
    },

    // ------------------------------ errors ------------------------------
    showError(err, fallbackKey) {
      const data = (err && err.response && err.response.data) || {};
      const msg = data.Msg || fallbackKey || "easmigration.failed";
      const translated = this.$t(msg);
      swal.fire({
        title: translated === msg ? msg : translated,
        text: data.Cause || this.getErrorText(data) || (err && err.message) || "",
        icon: "error",
        confirmButtonClass: "btn btn-danger btn-fill",
        buttonsStyling: false
      });
    },

    // ------------------------------ actions ------------------------------
    onCorporateChange() {
      this.preflight = null;
    },
    onTargetChange() {
      this.preflight = null;
    },

    async runPreflight() {
      this.busy = true;
      this.preflight = null;
      try {
        const resp = await WebPortalService.EasMigrationPreflightV4({
          enterprise_id: this.enterpriseId,
          target_eas: this.targetEas
        });
        this.preflight = resp.data;
      } catch (err) {
        // A blocked preflight answers 422 with the findings in the body —
        // that is the answer the operator asked for, not a failure to show.
        const data = err && err.response && err.response.data;
        if (data && (data.Blocking || data.Warnings)) {
          this.preflight = data;
        } else {
          this.showError(err, "easmigration.failed");
        }
      } finally {
        this.busy = false;
      }
    },

    // Step 2 of 3. The read pulls and stores everything and touches nothing;
    // the job then rests at READ_OK until an operator authorises the rest.
    async runRead() {
      this.busy = true;
      try {
        const resp = await WebPortalService.EasMigrationReadV4({
          enterprise_id: this.enterpriseId,
          target_eas: this.targetEas
        });
        this.migrationId = resp.data.MigrationId;
        this.progressOpen = true;
        this.startPolling();
        this.loadHistory();
      } catch (err) {
        this.showError(err, "easmigration.failed");
      } finally {
        this.busy = false;
      }
    },

    // Step 3 of 3, and the only destructive one.
    //
    // One sentence, deliberately. This dialog replaced a red banner and a
    // tick-box on the card, and the reason that trade works is that a warning
    // people actually read beats three they skip — so it does not get refilled
    // with everything the banner used to say.
    //
    // confirm_voicemail_loss is still sent on the request below. The API
    // requires it and the migration does destroy stored voicemail; what changed
    // is only that the operator is no longer asked to read a second warning at
    // the moment they are trying to start a job they have already decided on.
    async confirmStart() {
      const confirmed = await swal.fire({
        title: this.$t("easmigration.authorise_title"),
        text: this.$t("easmigration.outage_rule"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("easmigration.start_button"),
        cancelButtonText: this.$t("cancel"),
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-secondary btn-fill",
        buttonsStyling: false
      });
      if (!confirmed.value) return;

      this.busy = true;
      try {
        await WebPortalService.EasMigrationStartV4({
          migration_id: this.migrationId,
          confirm_voicemail_loss: "1"
        });
        this.progressOpen = true;
        this.startPolling();
      } catch (err) {
        this.showError(err, "easmigration.failed");
      } finally {
        this.busy = false;
      }
    },

    // Retry continues a failed job from where it stopped — the worker re-probes
    // every item against both EAS servers first — so the wording never says
    // "start over", and it states how much work is actually left.
    //
    // The count is stated only when the page holds every item (itemsComplete).
    // A truncated /status page would undercount, and a too-small "N items
    // remain" on a destructive confirmation is worse than no number, so the
    // wording without the count is used instead.
    //
    // When the job died inside the outage window the corporate is down right
    // now, and this button is what ends that. Saying so turns the dialog from
    // "would you like to finish this job" into "this is how the corporate comes
    // back", which is a different decision and a more urgent one.
    async confirmRetry() {
      const remaining = this.itemsComplete
        ? this.$t("easmigration.retry_text", { count: this.remainingItems })
        : this.$t("easmigration.retry_text_unknown");
      const confirmed = await swal.fire({
        title: this.$t("easmigration.retry_title"),
        text: this.outageOpen
          ? this.$t("easmigration.outage_open_failed") + "\n\n" + remaining
          : remaining,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("easmigration.retry_yes"),
        cancelButtonText: this.$t("cancel"),
        confirmButtonClass: "btn btn-warning btn-fill",
        cancelButtonClass: "btn btn-secondary btn-fill",
        buttonsStyling: false
      });
      if (!confirmed.value) return;

      this.busy = true;
      try {
        await WebPortalService.EasMigrationRetryV4({
          migration_id: this.migrationId
        });
        this.progressOpen = true;
        this.startPolling();
      } catch (err) {
        this.showError(err, "easmigration.failed");
      } finally {
        this.busy = false;
      }
    },

    // ------------------------ voicemail backup ------------------------
    //
    // Read every mailbox in the enterprise and compress it into one archive on
    // the server. It is a pre-migration safety step: the migration deletes the
    // source EAS Server's stored messages and nothing here can bring them back
    // afterwards — only a restore from this archive can, which is why the
    // archive is kept rather than downloaded and thrown away.
    //
    // The run therefore ENDS at phase `ready`. The Download button below is an
    // offer, not a step: the operator may take the zip now, or later from the
    // Voicemail Backup Log page, and either way the server still holds the copy
    // a restore replays.

    // Turns an axios rejection into the { title, detail } pair the popup shows.
    // describeExportError is reused rather than reimplemented because the
    // download leg hits the same trap the .xlsx export does — a JSON error body
    // delivered as a Blob, which a naive reader renders as "[object Blob]".
    vmDescribeError(err, fallbackKey) {
      return describeExportError(
        err,
        msg => this.$t(msg),
        this.$t(fallbackKey)
      );
    },

    // Every failure path lands here. The server's own ErrorLog is deliberately
    // NOT touched: it stays in this.vm.errorLog and the popup renders it
    // verbatim below the title, because it is the only record of which
    // mailboxes could not be read.
    failVoicemailBackup(step, title, detail) {
      this.stopVmPolling();
      this.vmErrorStep = step || this.vmStage;
      this.vmStage = VM_PHASE_ERROR;
      this.vmErrorTitle = title || this.$t("easmigration.vm_error_title");
      this.vmErrorDetail = detail || "";
    },

    async startVoicemailBackup() {
      // vmOpen is the re-entry guard as well as the button's disabled rule, so
      // it is tested before anything sets it.
      if (this.vmOpen || !this.enterpriseId || this.busy) return;

      this.vmJobId = 0;
      this.vmErrorStep = "";
      this.vmErrorTitle = "";
      this.vmErrorDetail = "";
      this.vmDownloading = false;
      this.vm = normaliseVmJob(null);
      this.vmStage = VM_PHASE_READ;
      this.vmOpen = true;

      try {
        const resp = await WebPortalService.VoicemailBackupStartV4({
          enterprise_id: this.enterpriseId
        });
        if (this.killed) return;
        // JobId sits at the top level of the /start envelope, not inside Data.
        // Same spelling in both places, so the one field table covers it.
        this.vmJobId = vmNum(resp.data && resp.data[VM_FIELDS.jobId]);
        if (this.vmJobId <= 0) {
          // "ok" without an id leaves nothing to poll or download. Saying so
          // beats polling an empty id every two seconds forever.
          this.failVoicemailBackup(
            VM_PHASE_READ,
            this.$t("easmigration.vm_no_job_id"),
            ""
          );
          return;
        }
        this.startVmPolling();
      } catch (err) {
        if (this.killed) return;
        const e = await this.vmDescribeError(err, "easmigration.vm_failed");
        if (this.killed) return;
        this.failVoicemailBackup(VM_PHASE_READ, e.title, e.detail);
      }
    },

    startVmPolling() {
      // Reached only after awaiting a request, so the component may already be
      // gone. Same rule as startPolling: `killed` is never cleared here.
      if (this.killed) return;
      this.stopVmPolling();
      this.vmPollOnce();
    },
    stopVmPolling() {
      if (this.vmPollTimer) {
        clearTimeout(this.vmPollTimer);
        this.vmPollTimer = null;
      }
    },

    // One poll. Every path out of it either stops the loop or schedules exactly
    // one more tick — there is no path that does both and none that does
    // neither.
    async vmPollOnce() {
      // (1) Before firing. The modal may have been closed or the component
      // destroyed since the timer was set.
      if (this.killed || !this.vmOpen || this.vmJobId <= 0) return;

      let data;
      try {
        const resp = await WebPortalService.VoicemailJobStatusV4({
          job_id: this.vmJobId
        });
        data = resp.data && resp.data.Data;
      } catch (err) {
        // (2) The request itself failed.
        if (this.killed) return;
        const e = await this.vmDescribeError(err, "easmigration.vm_failed");
        if (this.killed) return;
        this.failVoicemailBackup(this.vmStage, e.title, e.detail);
        return;
      }

      // (3) and (4) The request was in flight while the component was destroyed
      // or the popup was closed. There is nobody left to show the answer to,
      // and scheduling the next poll is exactly the leak this guard exists for.
      if (this.killed || !this.vmOpen) return;

      this.vm = normaliseVmJob(data);

      // (5) The worker said it failed. No title of its own: the server named
      // the failure in ErrorLog, which the popup renders in full underneath.
      if (this.vm.phase === VM_PHASE_ERROR) {
        this.failVoicemailBackup(this.vmStage, "", "");
        return;
      }
      // (6) The job is marked failed without a phase having said so. A worker
      // killed mid-run leaves exactly this — a row whose status its supervisor
      // wrote and whose phase is whatever it last managed to save — and a loop
      // watching only Phase would poll it forever.
      if (this.vm.statusName === VM_STATUS_FAILED) {
        this.failVoicemailBackup(
          this.vmStage,
          this.$t("easmigration.vm_worker_stopped"),
          ""
        );
        return;
      }
      if (this.vm.phase === VM_PHASE_READY) {
        this.stopVmPolling();
        // (7) Ready, but the archive is not on the disk. Terminal: polling on
        // would never change it. Said plainly, because the operator is about to
        // run a migration that destroys the originals and must not believe they
        // are holding a copy.
        if (!this.vm.downloadable) {
          this.failVoicemailBackup(
            VM_PHASE_READY,
            this.$t("easmigration.vm_file_missing"),
            ""
          );
          return;
        }
        // (8) Finished. The zip stays on the server; the footer now offers it.
        this.vmStage = VM_PHASE_READY;
        return;
      }

      // Still running — the only path that schedules another tick.
      this.vmStage =
        this.vm.phase === VM_PHASE_COMPRESS ? VM_PHASE_COMPRESS : VM_PHASE_READ;
      this.vmPollTimer = setTimeout(this.vmPollOnce, VM_POLL_MS);
    },

    // The Download button. Pressed by the operator, never fired automatically:
    // the archive is the thing a restore replays, so taking a copy is a
    // separate decision from having made one, and failing to save the file no
    // longer means the backup was lost.
    async downloadVoicemailBackup() {
      if (this.vmDownloading || !this.vmCanDownload) return;
      this.vmDownloading = true;

      let resp;
      try {
        resp = await WebPortalService.VoicemailJobDownloadV4({
          job_id: this.vmJobId
        });
        // The same trap the .xlsx export hits: a JSON error body that arrived
        // as a Blob on a 200, which without this check is saved as a ~90-byte
        // "zip" the operator only discovers is broken later.
        if (isJsonBody(resp)) {
          const parsed = await readBlobAsJSON(resp.data);
          const jsonError = new Error(
            "voicemail backup download returned a JSON error body"
          );
          jsonError.response = { data: parsed };
          throw jsonError;
        }
      } catch (err) {
        if (this.killed) return;
        // vmDescribeError, not showError: on this leg the server's JSON error
        // body arrives as a Blob (responseType is "blob"), and showError would
        // read Msg off the Blob, find nothing, and show the bare fallback.
        const e = await this.vmDescribeError(
          err,
          "easmigration.vm_download_failed"
        );
        if (this.killed) return;
        this.vmDownloading = false;
        // A sweetalert, NOT failVoicemailBackup: the backup itself succeeded
        // and its archive is still on the server. Only this attempt to save a
        // copy failed, and marking the run failed would tell the operator the
        // opposite of the truth right before they authorise a migration — and
        // would take away the Download button that lets them simply try again.
        swal.fire({
          title: e.title,
          text: e.detail,
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false
        });
        return;
      }
      if (this.killed) return;

      // Content-Disposition normally names the file; the fallback is built from
      // the enterprise id rather than its name, because a Thai corporate name
      // sanitises down to a row of underscores.
      downloadBlob(
        resp,
        this.vm.fileName || "voicemail-backup-" + this.enterpriseId + ".zip",
        VM_ZIP_MIME
      );
      this.vmDownloading = false;
    },

    // Closing is what stops the poll. It is only reachable once the run has
    // finished — the popup has no backdrop dismiss and no Close button before
    // then — but stopVmPolling() is called unconditionally rather than behind a
    // stage test, because "a timer outlived its modal" is the bug, and a guard
    // that happens to be right today is not the same as one that cannot be
    // wrong.
    closeVoicemailBackup() {
      this.stopVmPolling();
      this.vmOpen = false;
    },

    // ------------------------------ polling ------------------------------
    isTerminal(status) {
      // READ_OK is terminal too: the job rests there until someone authorises
      // it, so polling on would be an idle request every two seconds forever.
      return (
        status === JOB_SUCCESS ||
        status === JOB_FAILED ||
        status === JOB_READ_OK
      );
    },
    startPolling() {
      // Every caller reaches here only after awaiting a network request, so the
      // component may already have been destroyed while that request was in
      // flight. `killed` must therefore never be cleared here: doing so would
      // un-kill a dead instance and start an interval and a self-rescheduling
      // timeout that nothing is left to clear. It is set true in beforeDestroy
      // and in no other place, so there is nothing legitimate to reset.
      if (this.killed) return;
      this.stopPolling();
      this.tickTimer = setInterval(() => {
        if (this.progressOpen) this.elapsed += 1;
      }, 1000);
      this.pollOnce();
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.tickTimer) {
        clearInterval(this.tickTimer);
        this.tickTimer = null;
      }
    },
    async pollOnce() {
      if (this.killed || !this.migrationId) return;
      const ok = await this.fetchStatus();
      if (this.killed) return;
      if (!ok || (this.job && this.isTerminal(this.job.Status))) {
        this.stopPolling();
        this.progressOpen = false;
        return;
      }
      this.pollTimer = setTimeout(this.pollOnce, POLL_MS);
    },
    async fetchStatus() {
      try {
        const resp = await WebPortalService.EasMigrationStatusV4({
          migration_id: this.migrationId,
          per_page: STATUS_ITEMS
        });
        this.job = resp.data.Job;
        this.items = resp.data.Items || [];
        this.elapsed = (this.job && this.job.ElapsedSeconds) || 0;
        return true;
      } catch (err) {
        this.showError(err, "easmigration.failed");
        return false;
      }
    },
    async refreshStatus() {
      if (!this.migrationId) return;
      await this.fetchStatus();
    },

    async loadHistory() {
      try {
        const resp = await WebPortalService.EasMigrationListV4({
          page: 1,
          per_page: 20
        });
        this.history = (resp.data && resp.data.Data) || [];
        return true;
      } catch {
        this.history = [];
        return false;
      }
    },

    async resumeActiveJob() {
      if (!(await this.loadHistory())) return;
      const live = this.history.find(
        j => j.Status === JOB_QUEUED || j.Status === JOB_RUNNING
      );
      const resume = live || this.history.find(j => j.Status === JOB_READ_OK);
      if (!resume) return;
      this.migrationId = resume.Id;
      this.enterpriseId = resume.EnterpriseId;
      this.targetEas = resume.TargetEas;
      this.job = resume;
      if (live) {
        this.progressOpen = true;
        this.startPolling();
      } else {
        this.fetchStatus();
      }
    },

    // ------------------------------- logs -------------------------------
    openLogsTab() {
      this.activeTab = "logs";
      this.loadHistory();
      if (this.migrationId && this.logs.length === 0) this.searchLogs();
    },
    // Switching the job in the logs picker moves the whole page onto that job,
    // export and retry included — otherwise the buttons on the migration tab
    // would act on a different migration from the one being read.
    onLogJobChange() {
      this.logAttempt = "";
      // Another job is another set of phases and another set of functions, so
      // both the choices and the lists they were chosen from are dropped. The
      // reload refills them from the job now on screen.
      this.logPhase = "";
      this.logFunc = "";
      this.phaseFacets = [];
      this.funcFacets = [];
      this.refreshStatus();
      this.searchLogs();
    },
    // Element's el-select renders a bound value that no option carries as a
    // bare string, so a selection outliving its facet list is kept in the list.
    withSelection(list, selected) {
      const out = Array.isArray(list) ? list.slice() : [];
      if (selected && out.indexOf(selected) < 0) out.push(selected);
      return out;
    },
    searchLogs() {
      this.loadLogs(1);
    },
    onLogPageChange(p) {
      if (p === this.logPage) return;
      this.loadLogs(p);
    },
    async loadLogs(page) {
      if (!this.migrationId) return;
      this.loadingLogs = true;
      try {
        const resp = await WebPortalService.EasMigrationLogListV4({
          migration_id: this.migrationId,
          level: this.logLevel,
          attempt: this.logAttempt === "" ? "" : String(this.logAttempt),
          phase: this.logPhase,
          func_name: this.logFunc,
          page: page,
          per_page: this.logPerPage
        });
        this.logs = (resp.data && resp.data.Data) || [];
        this.logTotal = (resp.data && resp.data.Total) || 0;
        this.logPage = (resp.data && resp.data.Page) || page;
        this.logPerPage = (resp.data && resp.data.PerPage) || LOG_PER_PAGE;
        // Assigned only when the field really is a list. An empty job answers
        // with empty arrays, which is a true answer and must land; a response
        // that carries neither field must not silently wipe the pickers.
        if (Array.isArray(resp.data && resp.data.Phases)) {
          this.phaseFacets = resp.data.Phases;
        }
        if (Array.isArray(resp.data && resp.data.Functions)) {
          this.funcFacets = resp.data.Functions;
        }
      } catch (err) {
        this.logs = [];
        this.logTotal = 0;
        this.showError(err, "easmigration.failed");
      } finally {
        this.loadingLogs = false;
      }
    },
    openSoap(row) {
      this.soapPretty = this.formatXml(row.Soap || "");
      this.soapFuncName = row.FuncName || "";
      this.showSoapModal = true;
    },

    // --------------------------- snapshot rows ---------------------------
    //
    // eas_migration_snapshot.half records which SERVER the userIdentity named,
    // not which prefix the service indication carries: Meta_* repositories are
    // pulled from "<cfs>/<object>" and stored as cfs, Msph_* from
    // "<sourceEas>/<object>" and stored as eas. The label is what tells an
    // operator which of the two machines an envelope actually describes, so it
    // is shown as its own badge rather than left to be inferred from the SI.
    halfText(half) {
      const keys = {
        cfs: "easmigration.snapshot_half_cfs",
        eas: "easmigration.snapshot_half_eas"
      };
      const key = keys[half];
      return key ? this.$t(key) : this.$t("easmigration.snapshot_half_unknown");
    },
    halfBadge(half) {
      if (half === "eas") return "badge-dark";
      if (half === "cfs") return "badge-secondary";
      return "badge-light";
    },
    // Fetched here and nowhere else. /status is polled every two seconds while
    // a job runs and one item can carry several whole SOAP bodies, so the
    // payloads are asked for only when an operator opens a specific row.
    async openSnapshot(row) {
      this.snapshotObjectKey = row.ObjectKey || "";
      this.snapshotRows = [];
      this.snapshotTotal = 0;
      this.showSnapshotModal = true;
      this.loadingSnapshot = true;
      try {
        const resp = await WebPortalService.EasMigrationSnapshotV4({
          migration_id: this.migrationId,
          item_id: row.Id,
          // SNAPSHOT_ROWS is the endpoint's own ceiling, so one request holds
          // every repository of any item this system stores — a subscriber has
          // six, a re-read of the same job appends a second set. Total is still
          // compared against what arrived, because the ceiling is the server's
          // to change and a silently short list is the one failure this modal
          // cannot be allowed to have.
          per_page: SNAPSHOT_ROWS
        });
        const data = (resp.data && resp.data.Data) || [];
        this.snapshotTotal = (resp.data && resp.data.Total) || data.length;
        // The server resolved (migration_id, item_id) to the item row itself,
        // so its object key is the one the payloads were actually looked up by.
        if (resp.data && resp.data.ObjectKey) {
          this.snapshotObjectKey = resp.data.ObjectKey;
        }
        // Pretty-printed once, on arrival. Doing it from the template would
        // re-run the whole reformat on every re-render of the modal, and these
        // bodies are large enough for that to be felt.
        this.snapshotRows = data.map((s, i) => ({
          Key: s.Id ? "s" + s.Id : "i" + i,
          Half: s.Half,
          ServiceIndication: s.ServiceIndication,
          UserIdentity: s.UserIdentity,
          SequenceNumber: s.SequenceNumber,
          PulledAt: s.PulledAt,
          Pretty: this.formatXml(s.Payload || "")
        }));
      } catch (err) {
        // The dialog goes away before the error does: a swal over an empty
        // modal reads as though the modal itself had failed to close.
        this.showSnapshotModal = false;
        this.showError(err, "easmigration.failed");
      } finally {
        this.loadingSnapshot = false;
      }
    },
    copySnapshotRow(row) {
      this.writeClipboard(row.Pretty || "").catch(() => {});
    },
    // Same token-level reformat LogsTables.vue uses: the envelopes captured
    // here are well-formed and carry no mixed content, so no parser is needed.
    formatXml(xml) {
      if (!xml) return "";
      const PAD = "  ";
      const compact = xml.replace(/>\s+</g, "><").trim();
      const lines = compact.replace(/></g, ">\n<").split("\n");
      let indent = 0;
      const out = [];
      for (const raw of lines) {
        const line = raw.trim();
        if (!line) continue;
        const isClose = /^<\//.test(line);
        const isSelfClose = /\/>$/.test(line);
        const isOpenClose = /^<[^!?][^>]*>[^<]*<\/[^>]+>$/.test(line);
        const isDecl = /^<\?/.test(line) || /^<!/.test(line);
        if (isClose) indent = Math.max(0, indent - 1);
        out.push(PAD.repeat(indent) + line);
        if (!isClose && !isSelfClose && !isOpenClose && !isDecl) indent++;
      }
      return out.join("\n");
    },
    writeClipboard(text) {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        return navigator.clipboard.writeText(text);
      }
      return new Promise((resolve, reject) => {
        try {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          ta.remove();
          resolve();
        } catch (e) {
          reject(e);
        }
      });
    },
    copySoap() {
      this.writeClipboard(this.soapPretty || "").catch(() => {});
    }
  }
};
</script>

<style scoped>
/*
 * The four-pass strip.
 *
 * Four auto-placed cells fill row 1; .pass-bracket is then explicitly put in
 * columns 2 and 3 of row 2, so the red rule underneath lines up with exactly
 * the two passes the corporate is unreachable for. That alignment is the
 * content, not decoration: it is what shows the outage as one continuous span
 * rather than something that happens per step.
 */
.pass-timeline {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
}
.pass-step {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #f6f9fc;
  padding: 6px 8px;
  /* grid items default to min-width:auto and would refuse to shrink below
     their longest word, pushing the modal into a horizontal scroll. */
  min-width: 0;
}
.pass-step-name {
  display: block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  color: #525f7f;
  overflow-wrap: break-word;
}
.pass-step-hint {
  display: block;
  font-size: 10px;
  line-height: 1.3;
  color: #8898aa;
  overflow-wrap: break-word;
}
.pass-step-outage {
  background: #fff5f7;
  border-color: #fdd8e0;
}
.pass-step-outage .pass-step-name {
  color: #f5365c;
}
/* Behind, not greyed out of existence: a finished delete pass is still the
   reason the corporate is down. */
.pass-step.pass-done {
  opacity: 0.6;
}
.pass-step.pass-current {
  border-width: 2px;
  border-color: #5e72e4;
  padding: 5px 7px;
}
.pass-step-outage.pass-current {
  border-color: #f5365c;
}
/* The step a run stopped on. Same weight as .pass-current — a 2px border —
   because it occupies the same place in the strip and answers the same
   question ("where is it"); only the colour differs, so a glance at the strip
   tells current from stopped without reading the words. */
.pass-step.pass-failed {
  border-width: 2px;
  border-color: #f5365c;
  background: #fff5f7;
  padding: 5px 7px;
}
.pass-step.pass-failed .pass-step-name {
  color: #f5365c;
}
.pass-bracket {
  grid-column: 2 / span 2;
  border-top: 3px solid #f5365c;
  border-radius: 0 0 3px 3px;
  padding-top: 3px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #f5365c;
}
/* Below the modal's comfortable width the four columns stop being readable, so
   the passes stack and the bracket spans the lot. The alignment is lost, but
   the wording still names both passes and the banner above states the rule in
   full — the strip is reinforcement here, never the only place it is said. */
@media (max-width: 575.98px) {
  .pass-timeline {
    grid-template-columns: 1fr;
  }
  .pass-bracket {
    grid-column: 1;
  }
}
/*
 * READ_OK — "read finished, waiting for you to authorise".
 *
 * The three badges an operator scans this panel to tell apart are RUNNING
 * (badge-info), SUCCESS (badge-success) and this one. Compiled out of
 * src/assets/sass, those two are:
 *
 *   badge-info     #c2cbe4 on #ffffff   ($info is #e7eaf2, so the tint
 *                                        clamps to white — almost no fill)
 *   badge-success  #1aae6f on #b0edd3   (light mint fill, saturated green)
 *
 * so the theme's own idiom for a badge is a light tint with saturated text of
 * the same hue. #2643e9 is exactly what this theme's badge-variant mixin
 * yields for Argon's $blue (#5e72e4); the background is the same mixin's tint
 * taken a little deeper, so the pill carries the same weight on the card as
 * badge-success rather than fading into badge-info's white.
 *
 * Measured against those two, on white card stock:
 *
 *   text on fill        4.52:1   (badge-primary was 2.49:1)
 *   fill vs #ffffff     dE 26.1  (badge-success's fill is dE 27.5 from white,
 *                                 i.e. this reads as just as much of a pill)
 *   fill vs #b0edd3     dE 40.2
 *   text vs #c2cbe4     dE 96.8
 *
 * Different hue from success, and a fill where info has none, so no two of
 * the three collapse together.
 */
.badge-read-ok {
  color: #2643e9;
  background-color: #c9d0f7;
}
.export-error-detail {
  background: #fff5f7;
  border: 1px solid #f5365c;
  border-radius: 4px;
  padding: 10px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 60vh;
  overflow-y: auto;
  margin: 0;
}
/* One repository of a snapshot. Several stack in one modal, so each gets a
   rule above it and a shorter body than the single-envelope logs modal — at
   65vh apiece the second entry would start below the fold every time. */
.snapshot-entry + .snapshot-entry {
  border-top: 1px solid #e9ecef;
  padding-top: 12px;
}
.snapshot-entry .xml-detail {
  max-height: 40vh;
}
.xml-detail {
  background: #2d2d2d;
  color: #e8e8e8;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  white-space: pre;
  overflow: auto;
  max-height: 65vh;
  margin: 0;
}
</style>
